import { HandlerEvent } from 'post-robot';
import { error, ValidationError } from 'common/errors';
import completeCheckoutLegacy from 'communicator/external/checkout/integrated/legacy/complete-checkout-legacy';
import {
  getCheckoutData,
  getVIntentId,
  getMeta,
  getSelectCardPutIntentPayload,
  getAccessToken,
  getPutIntentResponse,
  getApiKey,
  getIsFeatureToggleEnabled
} from 'communicator/store/selectors';
import { fetchPutIntent } from 'communicator/utils/api/intent';
import { markStart, markEnd } from 'communicator/utils/instrumentation';
import {
  cvvLaunchHandler,
  dcfLaunchHandler,
  fidoLaunchHandler,
  launchIssuerOtpFlowHandler,
  srcOrchestrateBankAppHandler,
  threeDsLaunchHandler,
  vppLaunchHandler
} from 'communicator/utils/launch-handler-functions';

import { AssuranceData } from 'types/data';
import { CheckoutCommunicatorPayload, LaunchHandlers } from 'types/visa-sdk/checkout';

type CheckoutIssuerOtpFlowsData = {
  data: {
    assuranceData: AssuranceData;
  };
  launchHandlers: LaunchHandlers;
};

/**
 * Handles the completion of issuer OTP flows for checkout.
 * @param event - The event containing the checkout data and launch handlers.
 * @returns The checkout response payload or a validation error.
 */
export default async function IssuerOtpFlowsComplete(
  event: HandlerEvent<CheckoutIssuerOtpFlowsData>
): Promise<CheckoutCommunicatorPayload | ValidationError> {
  const vIntentId = getVIntentId(); // Get the Visa Intent ID
  const checkoutData = getCheckoutData(); // Get the checkout data stored in the communicator
  const cardIdMapping = getMeta().cardIdMapping; // Get the card ID mapping metadata

  // If checkout data is not available, return an error
  if (!checkoutData) {
    return error('CHECKOUT_FAILURE');
  }

  let selectedCardId;

  // Determine the selected card ID based on the checkout data
  if (checkoutData.srcDigitalCardId) {
    selectedCardId = cardIdMapping[checkoutData.srcDigitalCardId] ?? checkoutData.srcDigitalCardId;
  }

  // If selected card ID or Visa Intent ID is not available, mark the end of the process with an error
  if (!selectedCardId || !vIntentId) {
    markEnd('CHECKOUT_INTEGRATED', {
      doFlush: true,
      failure: true,
      reason: 'Unable to connect to DCF'
    });

    return error('Unable to connect to DCF');
  }

  // Mark the start of the checkout integrated put intent process
  markStart('checkout_integrated_put_intent');

  const accessToken = getAccessToken()?.token as string;
  const selectCardPutIntentPayload = await getSelectCardPutIntentPayload(selectedCardId);

  const sessionQualifier = getPutIntentResponse()?.headers['x-iam-session-qualifier'];

  //START GENAI@GHCOPILOT
  const isOrchestratorEnabled = getIsFeatureToggleEnabled('orchestratorCheckoutApi');
  const requestPayload = {
    ...selectCardPutIntentPayload,
    assuranceData: event.data.data.assuranceData
  };

  if (isOrchestratorEnabled && requestPayload.assuranceData?.verificationData) {
    // Create a new object with all existing properties
    const newAssuranceData = { ...requestPayload.assuranceData };

    // Add the verificationDatas property with the value from verificationData
    //eslint-disable-next-line @typescript-eslint/no-explicit-any
    (newAssuranceData as any).verificationDatas =
      requestPayload.assuranceData.verificationData;

    // Remove the original verificationData property
    delete newAssuranceData.verificationData;

    // Replace the assuranceData object
    requestPayload.assuranceData = newAssuranceData;
  }
  //END GENAI@GHCOPILOT

  const putIntentResponse = await fetchPutIntent({
    accessToken,
    apikey: getApiKey(),
    requestPayload,
    sessionQualifier,
    vIntentId,
    vsbFlow: checkoutData.vsbFlow || false
  });

  // Complete the legacy checkout process with the provided handlers
  const checkoutResponse = await completeCheckoutLegacy(
    { checkoutData, putIntentResponse },
    srcOrchestrateBankAppHandler(event.data.launchHandlers.launchSRCBankApp),
    launchIssuerOtpFlowHandler(event.data.launchHandlers.launchSRCIssuerOtp),
    cvvLaunchHandler(event.data.launchHandlers.launchCvv),
    dcfLaunchHandler(event.data.launchHandlers.launchDcf),
    fidoLaunchHandler(event.data.launchHandlers.launchFIDO),
    threeDsLaunchHandler(event.data.launchHandlers.launchThreeDs),
    threeDsLaunchHandler(event.data.launchHandlers.launchThreeDsWithOnLoad),
    vppLaunchHandler(event.data.launchHandlers.launchVPP),
    true
  );

  // Mark the end of the checkout process based on the response
  if (checkoutResponse.error) {
    markEnd('CHECKOUT_INTEGRATED', {
      doFlush: true,
      failure: true,
      reason: 'CHECKOUT_FAILURE'
    });
  } else {
    markEnd('CHECKOUT_INTEGRATED', { doFlush: true });
  }

  return checkoutResponse;
}
